import {
  Box,
  Chip,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Typography,
  styled
} from "@outschool/backpack";
import {
  ParentWaitlistedClassesQuery,
  ParentWaitlistedClassesQueryVariables
} from "@outschool/gql-frontend-generated";
import { faEllipsisV, faMessage } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  leaderPath,
  sectionEnrollPath,
  sectionOrActivityPath
} from "@outschool/routes";
import SectionCls from "@outschool/section";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  AvatarImage,
  DEFAULT_AVATAR,
  UserHeadshotImage
} from "@outschool/ui-components-shared";
import {
  IconButton,
  IconButtonProps
} from "@outschool/ui-legacy-component-library";
import { compact, max, partition } from "lodash";
import sortBy from "lodash/sortBy";
import React from "react";
import { useLocation } from "react-router";

import * as Activity from "../../shared/Activity";
import useSectionString from "../../shared/hooks/useSectionString";
import * as Meeting from "../../shared/Meeting";
import { shortenedLeaderName } from "../../shared/User";
import { useTimeZone } from "../providers/TimeZoneProvider";
import Link from "./Link";
import LoadingWithMessage from "./LoadingWithMessage";
// @ts-ignore - JSX import
import MessageModal from "./MessageModal";
import LeaveWaitlistButton from "./waitlists/LeaveWaitlistButton";

export type WaitlistSeatType = NonNullable<
  ParentWaitlistedClassesQuery["waitlistSeatsForParent"][number]
>;
export type WaitlistSectionType = WaitlistSeatType["section"];
export type WaitlistLeaderType = WaitlistSectionType["leader"];
export type WaitlistLearnerType = WaitlistSeatType["learner"];

const Section = new SectionCls();

const ClassImage = styled("img")({
  borderRadius: "8px",
  objectFit: "cover"
});

const WaitlistClassDescription = ({
  learner,
  activity,
  waitlistSeats,
  availableSpaces
}: WaitlistedClassesTileItem) => {
  const { timeZone } = useTimeZone();

  const { fullDurationStringFromGql } = useSectionString();

  const sections = waitlistSeats.map(seat => seat.section);

  const durationStrings = sections.map(section =>
    fullDurationStringFromGql({
      activity,
      section,
      nextOngoingMeeting: section?.nextOngoingMeeting,
      userTimeZone: timeZone,
      showTimes: true,
      showTz: false
    })
  );

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6">{activity.title}</Typography>
      <AvatarImage
        avatar={learner.avatar || DEFAULT_AVATAR}
        name={learner.name}
        sx={{ height: 24, width: 24, marginRight: 8, verticalAlign: "bottom" }}
      />
      <Typography variant="body1">{learner.name}</Typography>
      <SectionTimesText durationStrings={durationStrings} />
      <AvailableSpacesChip availableSpaces={availableSpaces} />
    </Box>
  );
};

const SectionTimesText = ({
  durationStrings
}: {
  durationStrings: string[];
}) => {
  const { t } = useTranslation("client\\components\\WaitlistedClassesList");
  const elems: JSX.Element[] = [];
  const numSections = durationStrings.length;
  const separator = (
    <Typography
      sx={{
        color: "grey.200",
        marginX: 8
      }}
    >
      •
    </Typography>
  );

  durationStrings.forEach((str, idx) => {
    elems.push(
      <Typography key={`elem-${idx}`} sx={{ display: "inline-block" }}>
        {str}
      </Typography>
    );
    if (idx !== numSections - 1) {
      elems.push(separator);
    }
  });

  return (
    <Typography>
      {numSections === 1 ? separator : <br />}
      {numSections > 1 &&
        t("Waitlisted in {{numSections}} sections:", { numSections }) + " "}
      {elems}
    </Typography>
  );
};

const AvailableSpacesChip = ({
  availableSpaces
}: Pick<WaitlistedClassesTileItem, "availableSpaces">) => {
  const { t } = useTranslation("client\\components\\WaitlistedClassesList");
  if (!availableSpaces) {
    return null;
  }
  return (
    <Box>
      <Chip
        color="success"
        size="small"
        label={t("{{availableSpaces}} available seat", {
          availableSpaces,
          count: availableSpaces,
          defaultValue_plural: "{{availableSpaces}} available seats"
        })}
      />
    </Box>
  );
};

interface UpcomingClassTeacherProps {
  leader: WaitlistLeaderType;
}

const UpcomingClassTeacher = ({ leader }: UpcomingClassTeacherProps) => (
  <Box
    component={Link}
    target="_blank"
    to={leaderPath(leader.uid, leader.leader_link)}
    onClick={(event: React.MouseEvent<HTMLElement>) => {
      // Stop link click from bubbling to link underneath
      event.stopPropagation();
    }}
    aria-label={leader?.name}
    flex
    sx={{
      alignItems: "center",
      gap: 8
    }}
  >
    <UserHeadshotImage user={leader} size={32} />
    <Typography
      variant="body2"
      sx={{
        color: "grey.600",
        width: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {leader.name}
    </Typography>
  </Box>
);

// This is gross but we need the Buttons in the overflow menu to match the style of the MenuItem
const BUTTON_INHERIT_SX: SxProps = {
  width: "100%",
  color: "inherit",
  textDecoration: "none !important",
  fontSize: "inherit",
  backgroundColor: "transparent",
  "&:hover:not(:disabled), &:focus:not(:disabled)": {
    // Match default MenuItem hover color
    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    color: "inherit !important"
  }
};

export enum WaitlistControl {
  EnrollInSection,
  WithdrawFromWaitlist,
  ManageWaitlist
}

type OverflowMenuButtonProps = Omit<IconButtonProps, "icon" | "onClick"> &
  WaitlistedClassesTileItem;

const OverflowMenuButton = ({
  activity,
  learner,
  waitlistSeats,
  availableSpaces,
  ...iconButtonProps
}: OverflowMenuButtonProps) => {
  const { t } = useTranslation("client\\components\\WaitlistedClassesList");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Stop event from bubbling to link underneath
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    // stop close event from propogating click event to link underneath
    event.stopPropagation();
    setAnchorEl(null);
  };

  const controlsToDisplay = !!availableSpaces
    ? [WaitlistControl.EnrollInSection, WaitlistControl.WithdrawFromWaitlist]
    : [WaitlistControl.ManageWaitlist];

  const menuItems = controlsToDisplay.map(control => {
    if (control === WaitlistControl.EnrollInSection) {
      return (
        <MenuItem
          key={control}
          component={Link}
          // Link props
          to={sectionEnrollPath(activity, waitlistSeats[0]?.section)}
          target="_blank"
          // Button Props
          variant="link"
          color="inherit"
          onClick={(e: any) => e.stopPropagation()}
          sx={BUTTON_INHERIT_SX}
        >
          {t("Enroll in section")}
        </MenuItem>
      );
    } else {
      return (
        <MenuItem
          key={control}
          component={LeaveWaitlistButton as any}
          // LeaveWaitlistButton props
          learner={learner}
          activity={activity}
          waitlistSeats={waitlistSeats}
          // Button props
          title={
            control === WaitlistControl.ManageWaitlist
              ? t("Manage waitlist")
              : t("Withdraw from waitlist(s)")
          }
          variant="link"
          color="inherit"
          onClick={(e: any) => e.stopPropagation()}
          sx={BUTTON_INHERIT_SX}
        />
      );
    }
  });

  return (
    <>
      <IconButton
        icon={faEllipsisV}
        onClick={handleClick}
        {...iconButtonProps}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        disableScrollLock
      >
        {menuItems}
      </Menu>
    </>
  );
};

type MessageTeacherButtonProps = {
  waitlistSeats: WaitlistSeatType[];
};

const MessageTeacherButton = ({ waitlistSeats }: MessageTeacherButtonProps) => {
  const { t } = useTranslation("client\\components\\WaitlistedClassesList");
  const location = useLocation();
  const { currentUser } = useSession();

  const [waitlistSeat] = waitlistSeats;
  const section = waitlistSeat?.section;

  if (!section) {
    return null;
  }

  const { activity, leader } = section;

  return (
    <MessageModal
      renderButton={(open: $TSFixMe) => (
        <IconButton icon={faMessage} onClick={open} />
      )}
      user={currentUser}
      location={location}
      requireLogin={true}
      isLoggedIn
      viewName="messageToLeader"
      heading={t("Message to {{teacherName}}", {
        teacherName: shortenedLeaderName(leader.name)
      })}
      recipients={[leader]}
      activityUid={activity.uid}
      sectionUid={section && section.uid}
      showFromAndTo
      subject={t("Message about: {{activityTitle}}", {
        activityTitle: activity.title
      })}
      context={{
        action: "Enrolled parent to teacher",
        activityTitle: activity.title,
        subject: "Message about: " + activity.title
      }}
    />
  );
};

const WaitlistedClassesTile = ({
  learner,
  activity,
  waitlistSeats,
  availableSpaces,
  sx: sxProps
}: WaitlistedClassesTileItem & {
  sx?: SxProps;
}) => {
  if (!learner) {
    return null;
  }

  const { details, leader } = activity;
  const { photo } = details;

  const tileLinkPath = !!availableSpaces
    ? sectionEnrollPath(activity, waitlistSeats[0]?.section)
    : sectionOrActivityPath(activity);

  return (
    <Box
      flex
      sx={[
        (theme: Theme) => ({
          paddingY: 16,
          paddingX: 24,
          gap: 8,
          borderColor: "grey.100",
          borderStyle: "solid",
          borderWidth: "1px",
          borderRadius: "8px",
          justifyContent: "space-between",
          flexDirection: "column",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            gap: 0
          },
          // reset link styles
          textDecoration: "none",
          color: "unset",
          "&:hover:not(:disabled), &:active:not(:disabled), &:focus:not(:disabled)":
            {
              textDecoration: "none",
              color: "unset"
            }
        }),
        ...(Array.isArray(sxProps) ? sxProps : [sxProps])
      ]}
      component={Link}
      to={tileLinkPath}
    >
      <Box
        flex
        sx={(theme: Theme) => ({
          gap: 8,
          alignItems: "flex-start",
          flexDirection: "column",
          [theme.breakpoints.up("md")]: {
            gap: 24,
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "wrap"
          }
        })}
      >
        <ClassImage
          src={photo ?? undefined}
          sx={(theme: Theme) => ({
            width: "100%",
            height: "auto",
            [theme.breakpoints.up("md")]: {
              width: 93,
              height: 88
            }
          })}
        />
        <WaitlistClassDescription
          learner={learner}
          activity={activity}
          waitlistSeats={waitlistSeats}
          availableSpaces={availableSpaces}
        />
      </Box>
      <Box
        flex
        sx={(theme: Theme) => ({
          gap: 16,
          alignItems: "center",
          justifyContent: "space-between",
          [theme.breakpoints.up("md")]: {
            justifyContent: "flex-start"
          }
        })}
      >
        <UpcomingClassTeacher leader={leader} />
        <Box
          flex
          sx={{
            alignItems: "center"
          }}
        >
          <MessageTeacherButton waitlistSeats={waitlistSeats} />
          <OverflowMenuButton
            learner={learner}
            activity={activity}
            waitlistSeats={waitlistSeats}
            availableSpaces={availableSpaces}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const ParentWaitlistedClasses = gql`
  query ParentWaitlistedClasses($parentUid: ID!) {
    waitlistSeatsForParent(userUid: $parentUid) {
      uid
      enrollment {
        uid
      }
      learner {
        uid
        name
        avatar
        age
      }
      section {
        uid
        canceled_at
        deleted_at
        end_time
        price_cents
        availableSpaces
        published_at
        start_time
        waitlistSeatsCount
        meetingDays
        activity {
          uid
          duration_weeks
          size_max
          hasTeacherSchedule
          isClub
          is_ongoing_weekly
          price_cents
          published_at
          title
          titleTranslated
          slug_id
          weekly_meetings
          isFlexSchedule
          isSelfPaced
          details {
            photo
          }
          leader {
            uid
            name
            photo
            leader_link
            timeZone
          }
        }
        leader {
          uid
          name
          photo
          leader_link
          timeZone
        }
        meetings {
          uid
          start_time
          end_time
        }
        nextOngoingMeeting {
          uid
          start_time
          end_time
        }
      }
    }
  }
`;

/**
 * Generates a string that uniquely indexes a single waitlist tile.
 * Determines the grouping for tiles, (learner+activity, and learner+
 * activity+section for tiles with availability).
 */
function getKeyForWaitlistMap(seat: WaitlistSeatType) {
  const learnerUid = seat.learner.uid;
  const activityUid = seat.section.activity.uid;
  const hasSpaces = seat.section.availableSpaces !== 0;
  const sectionUid = hasSpaces ? `_${seat.section.uid}` : "";
  return `${learnerUid}_${activityUid}${sectionUid}`;
}

interface WaitlistedClassesTileItem {
  key?: string;
  learner: WaitlistLearnerType;
  activity: WaitlistSectionType["activity"];
  waitlistSeats: WaitlistSeatType[];
  availableSpaces?: number;
}

function useWaitlistedClassesTiles() {
  const { currentUser, currentUserIsLoading } = useSession();

  // We skip the query if no parentUid, so we can assert this is non-null
  const parentUid = currentUser?.uid!;

  const { loading: queryLoading, data } = useQueryWithPreviousData<
    ParentWaitlistedClassesQuery,
    ParentWaitlistedClassesQueryVariables
  >(ParentWaitlistedClasses, {
    variables: { parentUid },
    skip: currentUserIsLoading || !parentUid
  });

  const loading = queryLoading || currentUserIsLoading;

  // Each "WaitlistedClassesTileItem" data structure corresponds
  // to an individual tile rendered on the page. A tile can contain
  // an individual or multiple waitlist seats.
  const waitlistTileItems = React.useMemo(() => {
    const filteredWaitlistSeats = data?.waitlistSeatsForParent.filter(
      seat => !seat?.section.canceled_at && !seat?.section.deleted_at
    );

    const waitlistSeats = compact(filteredWaitlistSeats);

    // Separate out seats where the section has available spaces
    const [seatsWithAvailability, seatsWithoutAvailability] = partition(
      waitlistSeats,
      seat => Section.publicallyAvailableSpaces(seat.section) !== 0
    );

    // Build a tile item for each seat with available spaces
    const tilesItemsWithAvailability =
      seatsWithAvailability.map<WaitlistedClassesTileItem>(seat => ({
        key: getKeyForWaitlistMap(seat),
        learner: seat.learner,
        activity: seat.section.activity,
        waitlistSeats: [seat],
        availableSpaces: Section.publicallyAvailableSpaces(seat.section)
      }));

    // Build a tile item for each unique learner+activity pair
    const tileItemMap = new Map<string, WaitlistedClassesTileItem>();
    seatsWithoutAvailability.forEach(seat => {
      const key = getKeyForWaitlistMap(seat);

      const {
        section: { activity },
        learner
      } = seat;
      const current: WaitlistedClassesTileItem = tileItemMap.get(key) ?? {
        key,
        learner,
        activity,
        waitlistSeats: []
      };
      current.waitlistSeats.push(seat);
      tileItemMap.set(key, { ...current });
    });
    const tileItemsWithoutAvailability = [...tileItemMap.values()];

    // Sort the tile items that don't have available spaces
    const sortedTileItemsWithoutAvailability = sortBy(
      tileItemsWithoutAvailability,
      [
        // Sort waitlist seats into groups with other activities, sorted by
        // whichever activity has the next occurring meeting.
        tileItem => {
          const tileItemsInSameActivity = tileItemsWithoutAvailability.filter(
            ({ activity }) => activity.uid === tileItem.activity.uid
          );
          const nextUpcomingMeetings = tileItemsInSameActivity.map(
            ({ waitlistSeats }) => max(waitlistSeats.map(getNextMeeting))
          );
          return max(nextUpcomingMeetings);
        }
      ]
    );

    // Return all tile items
    return [
      ...tilesItemsWithAvailability,
      ...sortedTileItemsWithoutAvailability
    ];
  }, [data]);

  return {
    loading,
    waitlistTileItems
  };
}

function getNextMeeting({ section }: WaitlistSeatType) {
  const { nextOngoingMeeting, meetings, activity } = section;
  if (nextOngoingMeeting) {
    return nextOngoingMeeting.start_time;
  }
  if (activity && Activity.isFlexSchedule(activity)) {
    return section?.start_time;
  }
  const nextMeeting = meetings.length
    ? Meeting.currentOrNextMeeting(meetings)
    : null;
  // If there are no next meetings we will order to the end (like groups),
  // otherwise use next meeting start time
  return nextMeeting?.start_time;
}

export function WaitlistedClassesList() {
  const { t } = useTranslation("client\\components\\WaitlistedClassesList");
  const { loading, waitlistTileItems } = useWaitlistedClassesTiles();

  return (
    <Box flex sx={{ flexDirection: "column", gap: 24 }}>
      {loading ? (
        <LoadingWithMessage>{t("Loading…")}</LoadingWithMessage>
      ) : waitlistTileItems.length === 0 ? (
        <Typography variant="body1" component="div">
          {t("You are not on any waitlists.")}
        </Typography>
      ) : (
        waitlistTileItems.map(
          ({ key, learner, activity, waitlistSeats, availableSpaces }) => (
            <WaitlistedClassesTile
              key={key}
              learner={learner}
              activity={activity}
              waitlistSeats={waitlistSeats}
              availableSpaces={availableSpaces}
            />
          )
        )
      )}
    </Box>
  );
}
